import { WORKFLOW_BOOKING_ACTION_KIND } from "@sagot/constants";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";
import { useMe } from "../../../session/use-me";
import { useConnectorEndpoints } from "./builder/use-connector-endpoints";
import { useRefs, type Option } from "./builder/refs";
import { loadTemplateLibrary, type PlaceholderName, type TemplateChoices, type TemplateEntry, type TemplateLibrary } from "./templates";

/** The library never changes while the console is open. */
export function useTemplateLibrary(): UseQueryResult<TemplateLibrary> {
  return useQuery({ queryKey: ["workflow-template-library"], queryFn: loadTemplateLibrary, staleTime: Infinity, retry: false });
}

export type TemplateOptions = Record<PlaceholderName, Option[]>;

/** Whether the template calls the customer's own system (a Data connector step): its endpoint must be set to "Only from my workflows". */
export function usesConnector(entry: TemplateEntry): boolean {
  return Object.values(entry.definition.paths).some((path) => path.steps.some((step) => step.type === "http_request"));
}

/** The options of one template: a template with a Data connector step is offered only the endpoints a connector request can call. */
export function optionsFor(entry: TemplateEntry, options: TemplateOptions, connectorEndpoints: readonly Option[]): TemplateOptions {
  return usesConnector(entry) ? { ...options, endpoint: [...connectorEndpoints] } : options;
}

/**
 * The rows of the workspace a template can point at, by placeholder: booking actions, webhook endpoints, teams, members
 * (the signed-in member first) and lead stages, and apart from the endpoints the ones a Data connector step can call.
 * Must run inside `RefsProvider`.
 */
export function useTemplateOptions(): { options: TemplateOptions; connectorEndpoints: Option[]; loading: boolean } {
  const refs = useRefs();
  const me = useMe();
  const connector = useConnectorEndpoints();
  const user = me.data?.user;
  const options = useMemo<TemplateOptions>(() => {
    const members = [...refs.members].sort((a, b) => Number(b.id === user?.id) - Number(a.id === user?.id));
    // Without the right to list members, the signed-in member is still a valid choice.
    const people = members.length > 0 ? members : user ? [{ id: user.id, name: user.name }] : [];
    return {
      booking_action: refs.actions.filter((action) => action.kind === WORKFLOW_BOOKING_ACTION_KIND).map((action) => ({ id: action.id, name: action.name })),
      endpoint: refs.endpoints,
      team: refs.teams,
      user: people,
      lead_stage: refs.stages,
    };
  }, [refs, user]);
  const connectorEndpoints = useMemo<Option[]>(
    () => connector.endpoints.filter((endpoint) => endpoint.enabled).map((endpoint) => ({ id: endpoint.id, name: endpoint.name })),
    [connector.endpoints],
  );
  return { options, connectorEndpoints, loading: refs.loading || me.isLoading || connector.loading };
}

/** The first row of each requirement unless the author picked another. */
export function resolveChoices(requires: readonly PlaceholderName[], options: TemplateOptions, picked: TemplateChoices): TemplateChoices {
  return Object.fromEntries(requires.map((name) => [name, picked[name] ?? options[name][0]?.id]));
}
